/* eslint-disable max-lines */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { isPhoneValidForForm } from '@components/atoms/ui-kit/phone-input.tsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import WizardStepper, { WizardStep } from '../shared/wizard-stepper';

import { useCreateCustomerBo } from '@utils/api/back-office/customer-bo/use-create-customer-bo';
import { useSearchPlansBo } from '@utils/api/back-office/plans-bo/use-search-plans-bo';
import { useGetResellerPlansBo } from '@utils/api/back-office/plans-bo/use-get-reseller-plans-bo';
import { useSearchResellersBo } from '@utils/api/back-office/customer-bo/use-search-resellers-bo';
import { CustomerBo } from '@utils/types/back-office/customer-bo';
import { PATHS } from '../../../router/paths';

import {
  WizardFormData,
  WizardUser,
  WizardSubOrg,
  CustomerType
} from './create-customer-wizard-types';
import { CustomerWizardStep1 } from './wizard-steps/customer-wizard-step1';
import { CustomerWizardStep2 } from './wizard-steps/customer-wizard-step2';
import { CustomerWizardStep3 } from './wizard-steps/customer-wizard-step3';
import { CustomerWizardStep4 } from './wizard-steps/customer-wizard-step4';
import { CustomerWizardReview } from './wizard-steps/customer-wizard-review';

interface CreateCustomerWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateCustomerWizard({
  isOpen,
  onClose
}: CreateCustomerWizardProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCustomerType, setSelectedCustomerType] =
    useState<CustomerType>('direct');
  const [planSearch, setPlanSearch] = useState('');
  const [resellerSearch, setResellerSearch] = useState('');
  const [selectedParentResellerId, setSelectedParentResellerId] = useState<
    number | undefined
  >(undefined);

  const createCustomerMutation = useCreateCustomerBo();

  const planQuery = useMemo(() => {
    if (!planSearch.trim()) return undefined;
    return `sku:ilike:%${planSearch.trim()}%`;
  }, [planSearch]);

  const { data: allPlansData, isLoading: isLoadingAllPlans } =
    useSearchPlansBo(planQuery);
  const { data: resellersData, isLoading: isLoadingResellers } =
    useSearchResellersBo(resellerSearch);
  const { data: resellerPlansData, isLoading: isLoadingResellerPlans } =
    useGetResellerPlansBo(selectedParentResellerId, planSearch || undefined);

  const resellers = useMemo(() => resellersData?.result ?? [], [resellersData]);

  const plans = useMemo(() => {
    if (selectedCustomerType === 'sub_org' && selectedParentResellerId) {
      return resellerPlansData?.result ?? [];
    }
    return allPlansData?.result ?? [];
  }, [
    selectedCustomerType,
    selectedParentResellerId,
    resellerPlansData,
    allPlansData
  ]);

  const isLoadingPlans =
    selectedCustomerType === 'sub_org' && selectedParentResellerId
      ? isLoadingResellerPlans
      : isLoadingAllPlans;

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('name-is-required')),
    email: Yup.string()
      .email(t('invalid-email'))
      .required(t('email-is-required')),
    phone: Yup.string()
      .nullable()
      .transform(value => (value?.trim() === '' ? null : value))
      .test(
        'is-valid-phone',
        t('enter-a-valid-international-phone-number'),
        isPhoneValidForForm
      ),
    is_reseller: Yup.boolean().default(false),
    parent_organization_id: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      ),
    max_users: Yup.number().default(3).min(1),
    users: Yup.array().of(
      Yup.object().shape({
        email: Yup.string()
          .email(t('invalid-email'))
          .required(t('email-is-required')),
        first_name: Yup.string().required(t('first-name-is-required')),
        last_name: Yup.string().required(t('last-name-is-required')),
        roles: Yup.array().of(Yup.string()).default(['admin'])
      })
    ),
    sub_organizations: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(t('name-is-required')),
        email: Yup.string()
          .email(t('invalid-email'))
          .required(t('email-is-required')),
        users: Yup.array().of(
          Yup.object().shape({
            email: Yup.string()
              .email(t('invalid-email'))
              .required(t('email-is-required')),
            first_name: Yup.string().required(t('first-name-is-required')),
            last_name: Yup.string().required(t('last-name-is-required')),
            roles: Yup.array().of(Yup.string()).default(['admin'])
          })
        )
      })
    )
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    trigger,
    reset,
    formState: { errors }
  } = useForm<WizardFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema as any),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      is_active: false,
      is_reseller: false,
      parent_organization_id: undefined,
      max_users: 3,
      auto_renew: true,
      users: [],
      sub_organizations: []
    }
  });

  const {
    fields: userFields,
    append: appendUser,
    remove: removeUser
  } = useFieldArray({ control, name: 'users' });

  const {
    fields: subOrgFields,
    append: appendSubOrg,
    remove: removeSubOrg
  } = useFieldArray({ control, name: 'sub_organizations' });

  const watchedValues = watch();

  const customerType: CustomerType = useMemo(() => {
    if (watchedValues.parent_organization_id) return 'sub_org';
    if (watchedValues.is_reseller) return 'reseller';
    return 'direct';
  }, [watchedValues.is_reseller, watchedValues.parent_organization_id]);

  const steps: WizardStep[] = useMemo(() => {
    const baseSteps = [
      { id: 1, label: t('organization-details') },
      { id: 2, label: t('plan-subscription') },
      { id: 3, label: t('users') }
    ];
    if (watchedValues.is_reseller && !watchedValues.parent_organization_id) {
      baseSteps.push({ id: 4, label: t('sub-organizations') });
    }
    baseSteps.push({ id: baseSteps.length + 1, label: t('review') });
    return baseSteps;
  }, [t, watchedValues.is_reseller, watchedValues.parent_organization_id]);

  const totalSteps = steps.length;
  const isReviewStep = currentStep === totalSteps;
  const isSubOrgsStep = watchedValues.is_reseller && currentStep === 4;

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 1:
        if (!watchedValues.name?.trim() || !watchedValues.email?.trim())
          return false;
        if (selectedCustomerType === 'sub_org') {
          return (
            watchedValues.parent_organization_id !== undefined &&
            watchedValues.parent_organization_id !== null
          );
        }
        return true;
      default:
        return true;
    }
  }, [currentStep, watchedValues, selectedCustomerType]);

  const handleNext = async () => {
    let isValid = true;
    if (currentStep === 1) isValid = await trigger(['name', 'email', 'phone']);
    else if (currentStep === 3) isValid = await trigger('users');
    else if (currentStep === 4 && isSubOrgsStep)
      isValid = await trigger('sub_organizations');
    if (isValid && currentStep < totalSteps) setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleClose = () => {
    reset();
    setCurrentStep(1);
    setSelectedCustomerType('direct');
    setSelectedParentResellerId(undefined);
    setPlanSearch('');
    setResellerSearch('');
    onClose();
  };

  const handleCustomerTypeSelect = (type: CustomerType) => {
    setSelectedCustomerType(type);
    if (type === 'direct') {
      setValue('is_reseller', false);
      setValue('parent_organization_id', undefined);
      setSelectedParentResellerId(undefined);
    } else if (type === 'reseller') {
      setValue('is_reseller', true);
      setValue('parent_organization_id', undefined);
      setSelectedParentResellerId(undefined);
    } else if (type === 'sub_org') {
      setValue('is_reseller', false);
    }
  };

  const handleCreate = async (data: WizardFormData) => {
    try {
      setIsSubmitting(true);
      const payload: Partial<CustomerBo> & {
        plan_id?: number;
        auto_renew?: boolean;
        users?: WizardUser[];
        sub_organizations?: WizardSubOrg[];
      } = {
        name: data.name,
        email: data.email,
        phone: data.phone || undefined,
        is_active: data.is_active,
        is_reseller: data.is_reseller,
        max_users: data.max_users
      };

      if (data.parent_organization_id) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (payload as any).parent_organization_id = data.parent_organization_id;
      }
      if (data.plan_id) {
        payload.plan_id = data.plan_id;
        payload.auto_renew = data.auto_renew;
      }
      if (data.users && data.users.length > 0) {
        payload.users = data.users.map(user => ({
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          roles: user.roles.length > 0 ? user.roles : ['admin']
        }));
      }
      if (
        data.is_reseller &&
        data.sub_organizations &&
        data.sub_organizations.length > 0
      ) {
        payload.sub_organizations = data.sub_organizations.map(subOrg => ({
          name: subOrg.name,
          email: subOrg.email,
          users:
            subOrg.users && subOrg.users.length > 0
              ? subOrg.users.map(u => ({
                  email: u.email,
                  first_name: u.first_name,
                  last_name: u.last_name,
                  roles: u.roles.length > 0 ? u.roles : ['admin']
                }))
              : undefined
        }));
      }

      const result = await createCustomerMutation.mutateAsync({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: payload as any
      });
      toast.success(t('customer-created-successfully'));
      setIsSubmitting(false);
      handleClose();
      if (result?.id) navigate(PATHS(String(result.id)).CUSTOMER_CONTENT);
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const err = error as any;
      const errorMessage =
        err?.response?.data?.error_description ||
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        t('error-creating-customer-please-try-again-later');
      setIsSubmitting(false);
      toast.error(errorMessage);
    }
  };

  const renderCurrentStep = () => {
    if (isReviewStep)
      return (
        <CustomerWizardReview
          watchedValues={watchedValues}
          customerType={customerType}
          plans={plans}
          resellers={resellers}
        />
      );
    if (isSubOrgsStep)
      return (
        <CustomerWizardStep4
          register={register}
          errors={errors}
          subOrgFields={subOrgFields}
          appendSubOrg={appendSubOrg}
          removeSubOrg={removeSubOrg}
        />
      );
    switch (currentStep) {
      case 1:
        return (
          <CustomerWizardStep1
            register={register}
            control={control}
            errors={errors}
            setValue={setValue}
            watchedValues={watchedValues}
            selectedCustomerType={selectedCustomerType}
            handleCustomerTypeSelect={handleCustomerTypeSelect}
            resellerSearch={resellerSearch}
            setResellerSearch={setResellerSearch}
            isLoadingResellers={isLoadingResellers}
            resellers={resellers}
            setSelectedParentResellerId={setSelectedParentResellerId}
          />
        );
      case 2:
        return (
          <CustomerWizardStep2
            control={control}
            setValue={setValue}
            watchedValues={watchedValues}
            plans={plans}
            isLoadingPlans={isLoadingPlans}
            planSearch={planSearch}
            setPlanSearch={setPlanSearch}
            selectedCustomerType={selectedCustomerType}
          />
        );
      case 3:
        return (
          <CustomerWizardStep3
            register={register}
            errors={errors}
            setValue={setValue}
            watchedValues={watchedValues}
            userFields={userFields}
            appendUser={appendUser}
            removeUser={removeUser}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      title={t('create-customer')}
      footerActions={[
        ...(currentStep > 1
          ? [
              <Button
                key="back"
                onClick={handleBack}
                label={t('back')}
                size="small"
                variant="smoke"
                icon={<ChevronLeft className="h-4 w-4" />}
                disabled={isSubmitting}
              />
            ]
          : []),
        isReviewStep ? (
          <Button
            key="create"
            onClick={handleSubmit(handleCreate)}
            label={isSubmitting ? t('creating') : t('create-customer')}
            size="small"
            variant="primary"
            disabled={isSubmitting}
          />
        ) : (
          <Button
            key="next"
            onClick={handleNext}
            label={t('next')}
            size="small"
            variant="smoke"
            icon={<ChevronRight className="h-4 w-4" />}
            iconPosition="right"
            disabled={!canProceed || isSubmitting}
          />
        )
      ]}
    >
      <div className="space-y-6">
        <WizardStepper
          steps={steps}
          currentStep={currentStep}
          onStepClick={setCurrentStep}
          allowNavigation
        />
        <div
          className="border-t pt-6"
          style={{ borderColor: getColour('hairline') }}
        >
          {renderCurrentStep()}
        </div>
      </div>
    </Modal>
  );
}
